import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import { Box, Button } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { ErrorOutline } from '@mui/icons-material';
import { Link } from 'react-router-dom';

import { selectOrganization, selectUser } from 'entities/user/selectors';
import { useNonprofitInfoQuery } from 'generated/graphql';
import { NonprofitOnboardingFlowContext } from 'components/Flow/flows/nonprofitOnboarding/context';
import { NonprofitOnboardingVerification } from 'components/Flow/flows/nonprofitOnboardingVerification';
import { usePartners } from 'entities/organization/usePartners';
import { getAllowedSteps } from 'components/Flow/flows/getAllowedSteps';
import { usePayPalVerificationState } from 'containers/VerificationRoute';
import { Privileges } from 'utils/privileges';
import { UserState } from 'entities/user/reducer';
import config from 'config';

const useStyles = makeStyles({ message: { flexGrow: 1 } });

const VerificationWarningInner = ({ user }: { user: UserState }) => {
  const styles = useStyles();
  const { data: nonprofitInfo, loading: nonprofitInfoLoading } = useNonprofitInfoQuery({
    skip: !user?.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS),
  });

  const [id, setId] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const { data: existingPartners } = usePartners(
    { status: ['pending', 'accepted'] },
    !user?.hasPrivilege(Privileges.PARTNER_VIEW)
  );
  const affiliatedWithWalmart = existingPartners.some(
    (partner) =>
      // @NOTE-DP: hardcode the wmt check for now... Dirty, but it's gonna be obsolete quite soon
      partner.name === 'Walmart'
  );

  const allowedSteps = getAllowedSteps(user);

  const state = usePayPalVerificationState(user);

  if (nonprofitInfoLoading) {
    return null;
  }

  // @NOTE-DP: only show the warning if the user would have landed in any of the PayPal verification states
  if (!(state?.value as { paypal: string })?.paypal) {
    return null;
  }

  if (!affiliatedWithWalmart) {
    return null;
  }

  if (nonprofitInfo?.nonprofitInfo?.hasVerifiedUsers !== false) {
    return null;
  }

  if (!allowedSteps.includes('verification')) {
    return null;
  }

  const hasAdmins = nonprofitInfo?.nonprofitInfo?.hasAdmins;

  // @NOTE-DP: if you are NOT an Admin AND there are other admins, no need to see the banner
  if (!user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS) && hasAdmins) {
    return null;
  }

  return (
    <>
      <Alert
        icon={<ErrorOutline fontSize="inherit" />}
        severity="error"
        style={{ marginBottom: '16px' }}
        classes={styles}
      >
        <Box display="flex">
          <Box flexGrow={1}>
            {hasAdmins ? (
              // @NOTE-DP: you are an Admin, but there are no verified Owners
              <>
                Your organization is missing a verified owner, which will impact your organization’s
                access to Spark Good programs. Owner verification is completed by entering your
                organization’s details in Deed. To complete owner verification, go to{' '}
                <Link to="/organizers">Settings &gt; Verification</Link> and follow the steps
                provided. If you need to assign a new user as the owner, please reach out to the
                <a href={`mailto:${config.nonprofitSuccessTeamEmail}`}>Nonprofit Success Team</a>.
                You can review your organization’s existing users here.
              </>
            ) : (
              // @NOTE-DP: that's the variant when you are an Organizer, and there are no other Admins
              <>
                Your organization is missing a verified owner. Owner verification is the process of
                having an admin complete the organization’s verification in Deed. To complete owner
                verification, go to <Link to="/organizers">Settings &gt; Verification</Link> and
                follow the steps provided. Failure to complete owner verification will result in all
                current users becoming blocked. This will affect your organization’s access to Spark
                Good programs until owner verification is complete.
              </>
            )}
          </Box>
          <Button
            style={{ minWidth: 135, textDecoration: 'underline', fontWeight: 500 }}
            variant="text"
            color="inherit"
            onClick={() => {
              setModalOpen(true);
            }}
          >
            Complete Verification Now
          </Button>
        </Box>
      </Alert>
      {modalOpen && (
        <NonprofitOnboardingFlowContext.Provider
          value={{
            isTransitionAllowed: (action) => action === 'DISMISS',
            send: (action) => {
              if (action === 'DISMISS') {
                setModalOpen(false);
                setId(id + 1);
              }
            },
            state: {},
          }}
        >
          <NonprofitOnboardingVerification
            send={(action) => {
              if (action === 'NEXT') {
                setModalOpen(false);
                setId(id + 1);
              }
            }}
            isTransitionAllowed={() => false}
            id={String(id)}
            standaloneMode
          />
        </NonprofitOnboardingFlowContext.Provider>
      )}
    </>
  );
};

export const VerificationWarning = () => {
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);

  if (!user) {
    return null;
  }

  if (organization?.type !== 'Nonprofit') {
    return null;
  }

  // @NOTE-DP: don't show the banner to already verified. We need to manually account for this, because `useNonprofitInfoQuery` won't reload automatically after verification, but user privileges would
  if (user.hasPrivilege(Privileges.ORGANIZATION_SELF_VERIFIED)) {
    return null;
  }

  return <VerificationWarningInner user={user} />;
};
